/**
 * All PostMint legal copy lives here so it can be edited without touching the design.
 *
 * HOW TO EDIT
 * - COMPANY: fill in the registration details. Anything left as "[TO BE COMPLETED]"
 *   is automatically hidden from the public site and shown only as an admin reminder.
 * - DOCS: each document is a list of sections. A section is { title, body?, list?, note? }.
 *   `note: "confirm"` renders the amber "confirm before publishing" style, so no unverified
 *   claim can slip out silently.
 */

export const TODO = "[TO BE COMPLETED]";

export const COMPANY = {
  brandName: "PostMint",
  tradingName: TODO,
  registeredName: TODO,
  registeredAddress: TODO,
  kvk: "42148900",
  vat: TODO,
  email: "mint@postmint.nl",
  website: "postmint.nl",
  telephone: TODO,
  country: "the Netherlands",
};

export const isFilled = (v) => Boolean(v) && v !== TODO && !v.includes("TO BE COMPLETED");

export const LAST_REVIEWED = TODO; // e.g. "1 July 2026"

const CONTACT_SECTION = {
  title: "Contact",
  body: `Questions about this document, your data or your subscription can be sent to ${COMPANY.email}. Our company details are listed on the Legal Notice page.`,
};

const REVIEW_SECTION = {
  title: "Review and updates",
  body: "We may update this document when the service, the law or our providers change. The version on this page is always the current one. Where a change materially affects you, we will inform you by email or in the app.",
  note: "confirm",
  noteText:
    "Legal review pending: have this document checked by a qualified Dutch/EU adviser before launch, and set the 'last reviewed' date in /app/frontend/src/data/legal.js.",
};

export const DOCS = {
  "privacy-policy": {
    title: "Privacy Policy",
    intro:
      "This Privacy Policy explains what personal data PostMint processes, why we process it, who we share it with and what rights you have under the GDPR (AVG).",
    sections: [
      {
        title: "Who is responsible for your data",
        body: `PostMint (${COMPANY.website}) is the controller for personal data we process about visitors, account holders and people who contact us. Where we process personal data on behalf of a business customer — for example the data inside content they create — we act as a processor and our Data Processing Agreement applies.`,
      },
      {
        title: "Personal data we may collect",
        list: [
          "Account information: your name, email address, business name, phone number if you provide it, and your language preference.",
          "Login and authentication information: your password in hashed form, session and token information, and — if you sign in with Google — the account identifier and profile details Google shares with us.",
          "Billing information: your plan, billing frequency, invoices and payment status. Card and payment details are entered with and processed by our payment provider, not stored by PostMint.",
          "Business information you enter into PostMint: your Brand Profile, tone of voice, services, opening hours, offers and anything else you type into the product.",
          "Social media account information: the account name and the access tokens issued by a platform when you choose to connect it, so scheduled content can be published on your instruction.",
          "Content: text, captions, prompts, uploaded photos, generated visuals, calendars, approvals and publishing history.",
          "Technical information: IP address, browser and device information, server and security logs, and error diagnostics.",
          "Website and product usage information, where analytics is used and you have consented to it.",
          "Support communications: the messages, tickets and attachments you send us.",
        ],
      },
      {
        title: "Why we process it, and the lawful basis",
        list: [
          "Creating and managing your account — performance of a contract.",
          "Providing the PostMint service, including AI-assisted content generation, scheduling and publishing content you have approved — performance of a contract.",
          "Processing subscriptions, payments, invoices and trials — performance of a contract and legal obligation.",
          "Providing customer support — performance of a contract.",
          "Security, fraud prevention, abuse prevention and troubleshooting — legitimate interests.",
          "Improving and debugging the product — legitimate interests.",
          "Complying with legal, tax and accounting obligations — legal obligation.",
          "Sending marketing email, and setting analytics or marketing cookies — only with your consent, which you can withdraw at any time.",
        ],
      },
      {
        title: "AI-assisted content",
        body: "When you ask PostMint to generate or review content, the relevant prompt, Brand Profile context and any photo you select are sent to our AI provider so the output can be produced. Generated content may contain errors and is presented to you as a draft for review — you decide whether it is approved or published.",
      },
      {
        title: "Retention",
        body: "We keep account and content data for as long as your account exists. When you delete your account, account data and content are deleted from our live systems, except where we must keep records to meet legal obligations (for example invoices for tax purposes) or to resolve a dispute. Backups and logs are removed on their own rolling cycle.",
        note: "confirm",
        noteText:
          "Set your exact retention periods here (live data, backups, logs, invoices) once your hosting and backup configuration is final.",
      },
      {
        title: "Who we share personal data with",
        list: [
          "Hosting and cloud infrastructure providers that run the PostMint application and database.",
          "Our payment provider, which processes subscription payments and card details.",
          "Our email provider, which delivers account and support email.",
          "Our AI provider(s), which process prompts and selected photos to generate content.",
          "Analytics providers, only where analytics is in use and you have consented.",
          "The social media platforms you choose to connect, for the content you approve.",
          "Professional advisers, and public authorities where we are legally required to disclose information.",
        ],
        note: "confirm",
        noteText:
          "Name each provider and its role in the Subprocessor List on the Data Processing Agreement page before launch. Do not publish provider names that have not been confirmed.",
      },
      {
        title: "International transfers",
        body: "Some providers may process data outside the European Economic Area. Where that is the case, we rely on an appropriate GDPR transfer mechanism — such as an adequacy decision or the European Commission's Standard Contractual Clauses — together with any additional measures required.",
        note: "confirm",
        noteText:
          "List which providers process data outside the EEA and which transfer mechanism applies to each, once your provider set is confirmed.",
      },
      {
        title: "Security",
        body: "We use technical and organisational measures to protect personal data, including encrypted connections to the application, hashed passwords and access controls. See the Trust & Security page for what is currently in place.",
      },
      {
        title: "Your rights",
        list: [
          "Access — ask for a copy of the personal data we hold about you.",
          "Rectification — have inaccurate data corrected.",
          "Erasure — ask us to delete your data, and delete your account yourself from Settings → Privacy & Data.",
          "Restriction — ask us to limit how we process your data.",
          "Objection — object to processing based on legitimate interests, and to direct marketing at any time.",
          "Portability — receive your data in a structured, machine-readable format.",
          "Withdraw consent — where processing is based on consent, withdraw it at any time without affecting processing that already took place.",
          "Complain — lodge a complaint with the Dutch Data Protection Authority (Autoriteit Persoonsgegevens) or the supervisory authority in your country.",
        ],
      },
      {
        title: "Cookies",
        body: "Our use of cookies and similar technologies is described in the Cookie Policy. Non-essential cookies are only set after you consent, and you can change your choice at any time via 'Cookie settings' in the footer.",
      },
      {
        title: "Children",
        body: "PostMint is a business tool and is not intended for children. We do not knowingly collect personal data from children.",
      },
      { ...CONTACT_SECTION, body: `Privacy questions and data-rights requests: ${COMPANY.email}.` },
      REVIEW_SECTION,
    ],
  },

  "cookie-policy": {
    title: "Cookie Policy",
    intro:
      "This policy explains the cookies and similar technologies used on the PostMint website and in the application, and how you control them.",
    sections: [
      {
        title: "Your choice comes first",
        body: "Only strictly necessary cookies are set when you arrive. Preference, analytics and advertising cookies are not activated until you give consent, and they are never pre-selected. You can accept all, reject everything non-essential, or choose per category — and change your mind later using 'Cookie settings' in the footer.",
      },
      {
        title: "Strictly necessary / functional cookies",
        list: [
          "Purpose: keeping you signed in, protecting forms and the login process, and remembering your cookie choice.",
          "Consent required: no — the service cannot work without them.",
          "Data collected: a session or token identifier, your consent record, and basic security information.",
          "Retention: for the browser session, or until your sign-in expires or your consent record is replaced.",
          "Provider: PostMint (first-party).",
        ],
      },
      {
        title: "Preference cookies",
        list: [
          "Purpose: remembering choices such as your interface language.",
          "Consent required: yes, where the preference is not needed to deliver the service you asked for.",
          "Data collected: the preference value only.",
          "Retention: [TO BE COMPLETED]",
          "Provider: PostMint (first-party).",
        ],
      },
      {
        title: "Analytics cookies",
        list: [
          "Purpose: understanding how the website is used so we can improve it.",
          "Consent required: yes.",
          "Data collected: pages visited, approximate location, device and browser information, and interaction events.",
          "Retention: [TO BE COMPLETED]",
          "Provider: [TO BE COMPLETED]",
        ],
        note: "confirm",
        noteText:
          "No analytics tooling is loaded on this site today. Add the provider, cookie names and retention here at the same time as you enable it — and only load it when consent.analytics is true.",
      },
      {
        title: "Advertising / tracking cookies",
        list: [
          "Purpose: measuring campaigns and showing relevant advertising.",
          "Consent required: yes.",
          "Data collected: campaign identifiers, clicks and conversions.",
          "Retention: [TO BE COMPLETED]",
          "Provider: [TO BE COMPLETED]",
        ],
        note: "confirm",
        noteText:
          "No advertising or tracking pixels are loaded on this site today. Complete this section before adding any.",
      },
      {
        title: "Changing or withdrawing your choice",
        body: "Select 'Cookie settings' in the footer to reopen the consent panel at any time. You can also delete cookies in your browser settings; the consent banner will then appear again on your next visit.",
      },
      CONTACT_SECTION,
      REVIEW_SECTION,
    ],
  },

  terms: {
    title: "Terms of Service",
    intro:
      "These Terms of Service apply to your use of the PostMint website and application. Please read them together with our Privacy Policy, Acceptable Use Policy and Refund & Cancellation Policy.",
    sections: [
      {
        title: "Who may use PostMint",
        body: "PostMint is offered to businesses and professionals. You must be at least 18 years old and able to enter into a binding agreement. If you use PostMint on behalf of an organisation, you confirm you are authorised to accept these terms for it.",
      },
      {
        title: "Your account",
        body: "You provide accurate registration details and keep them up to date. You are responsible for your login credentials and for everything that happens under your account. Tell us promptly at " + COMPANY.email + " if you suspect unauthorised access.",
      },
      {
        title: "What the service does",
        body: "PostMint helps you create, plan, approve and publish social media content. Depending on your plan this includes AI-assisted content and visuals, a Brand Profile, a content calendar, scheduling, approval workflows, connected social accounts and insights.",
      },
      {
        title: "AI-assisted content — your review matters",
        list: [
          "Content produced by PostMint is AI-assisted and may occasionally be inaccurate, incomplete or unsuitable for your business.",
          "Every piece of content is presented as a draft for you to review. You remain in control.",
          "Content is only scheduled and published after you approve it.",
          "You are responsible for checking that content is accurate and that it complies with applicable law, platform rules, intellectual property rights and advertising and consumer rules — including rules on pricing claims, health claims and disclosure of commercial content.",
        ],
      },
      {
        title: "Connected social platforms and third-party integrations",
        body: "You choose which social accounts to connect and you may disconnect them at any time. Those platforms are operated by third parties under their own terms, and their availability, permissions and rules can change. We are not responsible for a third-party platform's decisions, outages or policy changes.",
        note: "confirm",
        noteText:
          "Automatic publishing to Instagram, Facebook, LinkedIn and TikTok is currently simulated inside PostMint while platform app access is being reviewed. Keep this disclosure accurate as integrations go live.",
      },
      {
        title: "Availability and maintenance",
        body: "We aim to keep PostMint available and reliable, but the service is provided without an uptime guarantee unless separately agreed in writing. We may carry out maintenance, and we will try to schedule planned work to limit disruption.",
      },
      {
        title: "Plans, trials, pricing and billing",
        list: [
          "Plan features, limits and prices are shown on the Pricing page and in your dashboard before you pay.",
          "A free trial lasts 7 days and requires a valid payment card. You are charged nothing during the trial. When it ends, the plan you selected starts automatically and your card is charged for the first period, unless you cancel before the trial ends. We email you before the first charge. If a payment fails we retry for a few days and then pause the account; your content is not deleted.",
          "Prices on the website are shown excluding VAT where VAT applies. The exact VAT treatment is calculated and shown at checkout and on your invoice.",
          "Paid plans are billed in advance for the chosen period (monthly or annual) and renew automatically for the same period until cancelled.",
          "You can cancel at any time from Dashboard → Subscription. Cancellation and refunds are described in the Refund & Cancellation Policy.",
          "When a subscription ends, your access to paid features stops. Your content remains available in line with our retention wording until you delete your account.",
        ],
      },
      {
        title: "Acceptable use",
        body: "You may not use PostMint for illegal, abusive, fraudulent, infringing, deceptive or malicious purposes. The Acceptable Use Policy forms part of these terms.",
      },
      {
        title: "Intellectual property",
        list: [
          "You keep all rights in the content and material you upload or enter, and in the content you approve for your own channels.",
          "You grant PostMint a limited licence to host, process, adapt, transmit and display that material only to provide the service to you — for example to generate a caption, build a calendar or publish an approved post.",
          "PostMint and its licensors own the platform, software, models integrations, design, brand and website. Nothing in these terms transfers those rights to you.",
        ],
      },
      {
        title: "Suspension and termination",
        body: "We may suspend or terminate access if you seriously or repeatedly breach these terms or the Acceptable Use Policy, if required by law, or if your payment fails and remains unpaid after we have contacted you. Where reasonably possible we will notify you first and give you a chance to put things right.",
      },
      {
        title: "Warranties and liability",
        body: "PostMint is provided with reasonable care and skill. Beyond that, and to the extent permitted by law, the service is provided without further warranties — in particular we do not guarantee any specific commercial result, reach or engagement from published content. Our liability is limited to the extent permitted by law. Nothing in these terms excludes or limits liability for intent or gross negligence, for death or personal injury, or any mandatory rights you have under Dutch or EU law — including mandatory consumer rights, which always take precedence over these terms.",
      },
      {
        title: "Changes to the service and to these terms",
        body: "We may develop, change or discontinue features. If we make a material change to these terms or to a plan you are on, we will let you know in advance and you may cancel before the change takes effect.",
      },
      {
        title: "Governing law and disputes",
        body: `These terms are governed by Dutch law. Disputes may be brought before the competent court in ${COMPANY.country}, without affecting any mandatory right you have to bring proceedings elsewhere or to approach a consumer dispute body.`,
      },
      CONTACT_SECTION,
      REVIEW_SECTION,
    ],
  },

  dpa: {
    title: "Data Processing Agreement (DPA)",
    intro:
      "This Data Processing Agreement applies where PostMint processes personal data on behalf of a business customer (the controller) and forms part of the Terms of Service. It is drafted to meet Article 28 GDPR.",
    sections: [
      {
        title: "1. Subject matter and duration",
        body: "PostMint processes personal data on the customer's behalf in order to provide the PostMint service. Processing lasts for as long as the customer's account exists, plus any short period needed to complete deletion or to comply with a legal obligation.",
      },
      {
        title: "2. Nature and purpose of processing",
        body: "Hosting, storing, organising, generating, adapting, transmitting, scheduling, publishing to platforms selected by the customer, supporting the customer and securing the service.",
      },
      {
        title: "3. Categories of personal data",
        list: [
          "Account and contact details of the customer's users.",
          "Business information the customer enters, including Brand Profile content.",
          "Content, captions, prompts, uploaded photos and publishing history, which may contain personal data such as images of identifiable people or names in captions.",
          "Social account identifiers and access tokens for the platforms the customer connects.",
          "Technical and log data generated by use of the service.",
        ],
      },
      {
        title: "4. Categories of data subjects",
        list: [
          "The customer's own staff and account users.",
          "The customer's clients, guests or audience where they appear in submitted or generated content.",
          "People who interact with the customer's published content.",
        ],
      },
      {
        title: "5. Processing on documented instructions",
        body: "PostMint processes personal data only on the customer's documented instructions, which include the Terms of Service, this DPA, the settings the customer chooses and the actions the customer takes in the product. PostMint will inform the customer if, in its opinion, an instruction infringes data protection law, and will not use the customer's personal data for its own unrelated purposes.",
      },
      {
        title: "6. Confidentiality",
        body: "PostMint ensures that persons authorised to process the customer's personal data are bound by confidentiality and process it only as needed to perform their role.",
      },
      {
        title: "7. Technical and organisational measures",
        body: "PostMint implements appropriate technical and organisational measures having regard to the state of the art, the costs of implementation and the risks involved. The measures currently in place are described on the Trust & Security page.",
        note: "confirm",
        noteText:
          "Attach or restate your final security measures here (Annex II style) once verified. Do not claim measures that are not implemented.",
      },
      {
        title: "8. Subprocessors",
        body: "The customer gives general authorisation for PostMint to engage subprocessors for hosting, payment processing, email delivery, AI generation and similar functions. PostMint imposes data protection obligations on each subprocessor that are no less protective than this DPA, remains responsible for their performance, and will inform the customer of intended changes so the customer can object.",
      },
      {
        title: "9. Subprocessor list",
        list: [
          "Hosting / infrastructure: [TO BE COMPLETED] — role: running the application and database — location: [TO BE COMPLETED]",
          "Payment processing: [TO BE COMPLETED] — role: subscription payments and invoices — location: [TO BE COMPLETED]",
          "Email delivery: [TO BE COMPLETED] — role: account, trial and support email — location: [TO BE COMPLETED]",
          "AI content generation: [TO BE COMPLETED] — role: generating text, analysing selected photos, generating visuals — location: [TO BE COMPLETED]",
          "Analytics: [TO BE COMPLETED] — role: website usage statistics (consent-based) — location: [TO BE COMPLETED]",
        ],
        note: "confirm",
        noteText:
          "Complete this list with the actual provider names, roles and processing locations before launch. Keep it versioned so customers can see changes.",
      },
      {
        title: "10. International transfers",
        body: "Where a subprocessor processes personal data outside the EEA, PostMint puts an appropriate GDPR transfer mechanism in place, such as an adequacy decision or the European Commission's Standard Contractual Clauses, together with any supplementary measures required.",
      },
      {
        title: "11. Assisting with data subject requests",
        body: "PostMint provides the customer with the functionality and, where needed, reasonable assistance to respond to requests for access, rectification, erasure, restriction, portability and objection. If a data subject contacts PostMint directly about the customer's data, PostMint refers them to the customer.",
      },
      {
        title: "12. Personal data breaches",
        body: "PostMint notifies the customer without undue delay after becoming aware of a personal data breach affecting the customer's personal data, provides the information reasonably available to support the customer's own notification duties, and takes reasonable steps to mitigate the breach.",
      },
      {
        title: "13. DPIAs and prior consultation",
        body: "On request, and taking into account the nature of processing and the information available to it, PostMint provides reasonable assistance with data protection impact assessments and with prior consultation of a supervisory authority.",
      },
      {
        title: "14. Deletion or return after termination",
        body: "On termination the customer may export their data from the product. After termination, PostMint deletes the customer's personal data from live systems, except to the extent storage is required by law. Backups are removed on their normal cycle.",
        note: "confirm",
        noteText: "State the concrete deletion and backup-cycle timelines once your infrastructure is final.",
      },
      {
        title: "15. Audits and information rights",
        body: "PostMint makes available the information reasonably necessary to demonstrate compliance with Article 28 GDPR and allows for audits, including inspections, by the customer or an independent auditor mandated by the customer, subject to reasonable notice, confidentiality, and protection of other customers' data and of PostMint's security.",
      },
      {
        title: "16. Order of precedence",
        body: "If this DPA conflicts with the Terms of Service in relation to processing personal data on the customer's behalf, this DPA prevails.",
      },
      { ...CONTACT_SECTION, body: `To request a signed copy of this DPA, contact ${COMPANY.email}.` },
      REVIEW_SECTION,
    ],
  },

  "acceptable-use": {
    title: "Acceptable Use Policy",
    intro:
      "This policy sets out what you may not do with PostMint. It forms part of the Terms of Service and applies to every account, including trials.",
    sections: [
      {
        title: "Prohibited use",
        list: [
          "Illegal activity, or content that breaks applicable law.",
          "Fraud, scams, deceptive offers or impersonating a person, business or public body.",
          "Harassment, bullying, threats, or targeting individuals.",
          "Hateful, discriminatory, violent or otherwise abusive content.",
          "Infringing someone else's copyright, trademarks, image rights or other intellectual property — including uploading photos you have no right to use.",
          "Publishing personal data of others without a lawful basis, including recognisable images of people who have not agreed to it.",
          "Malware, phishing, credential theft, scraping, penetration attempts, or any other cybersecurity abuse.",
          "Spam, bulk unsolicited messaging, or misuse of contact data.",
          "Platform manipulation: fake engagement, bought followers, engagement pods, coordinated inauthentic behaviour.",
          "Deceptive social media practices, such as hiding paid partnerships or advertising that must be disclosed.",
          "Adult content, gambling, weapons, drugs or other material a connected platform forbids.",
          "Attempting to bypass PostMint security, plan limits, rate limits or usage allowances, or accessing another customer's data.",
          "Using PostMint in a way that breaks the rules of a connected social platform, or that would put our platform access at risk.",
          "Reselling or white-labelling PostMint output as an unlicensed service without our written agreement.",
        ],
      },
      {
        title: "Reporting abuse",
        body: `If you believe PostMint is being used in breach of this policy, email ${COMPANY.email} with as much detail as you can.`,
      },
      {
        title: "Enforcement",
        body: "We may investigate suspected breaches and, proportionate to the seriousness, warn you, remove content, restrict a feature, suspend the account or terminate it. Where the law requires it we will report content to the competent authority.",
      },
      CONTACT_SECTION,
      REVIEW_SECTION,
    ],
  },

  "refund-policy": {
    title: "Refund & Cancellation Policy",
    intro:
      "This page explains how to cancel your PostMint subscription, when cancellation takes effect and when a refund may be available.",
    sections: [
      {
        title: "How to cancel",
        body: "Go to Dashboard → Subscription and choose 'Cancel subscription'. You can also email " + COMPANY.email + " from your account address and we will process it for you. We do not require a phone call and we will not make cancelling deliberately difficult.",
      },
      {
        title: "When cancellation takes effect",
        body: "Cancellation takes effect at the end of the period you have already paid for. You keep access to your paid features until then, and no further payment is taken afterwards.",
      },
      {
        title: "What happens to your access and data",
        body: "After the paid period ends, generating, scheduling and publishing stop. Your content, calendar and Brand Profile remain visible in your account so you can export them, and are deleted when you delete your account. See the Privacy Policy for retention.",
      },
      {
        title: "Unused periods",
        body: "Subscriptions are paid in advance for the chosen period. Cancelling part-way through a paid month or year does not automatically create a refund for the remaining time.",
        note: "confirm",
        noteText:
          "Commercial decision needed: decide whether you pro-rate unused annual periods. Replace this section with the final rule before launch.",
      },
      {
        title: "Trials",
        body: "The 7-day trial is free but requires a card. Cancel at any point during the trial and you are not charged at all. If you do nothing, the plan you chose starts when the trial ends and the first payment is taken — we email you before that happens.",
      },
      {
        title: "Statutory right of withdrawal",
        body: "Consumers in the EU normally have 14 days to withdraw from a distance contract. Because PostMint is a digital service that starts immediately, we ask you at checkout to confirm that you want it to start straight away and that you acknowledge you lose the right of withdrawal once the service has been fully performed. Where the withdrawal right does apply, contact us within 14 days at " + COMPANY.email + " and we will handle it in line with the law. Business customers do not have a statutory withdrawal right.",
      },
      {
        title: "When we do give refunds",
        list: [
          "If we charged you in error or took a duplicate payment.",
          "If a paid feature was unavailable for a significant period through our fault and we could not fix it.",
          "Where consumer law or your statutory rights require a refund.",
        ],
        note: "confirm",
        noteText:
          "Commercial decision needed: add any goodwill refund window you want to offer (for example 'first 14 days of a first paid month'), or delete this note if the list above is your final policy.",
      },
      {
        title: "How refunds are paid",
        body: "Approved refunds are returned to the original payment method through our payment provider. Timing depends on your bank or card issuer.",
      },
      CONTACT_SECTION,
      REVIEW_SECTION,
    ],
  },

  "trust-security": {
    title: "Trust & Security",
    intro:
      "How we protect your account and your content today. We only list measures that are actually in place — anything still to be verified is marked.",
    sections: [
      {
        title: "Account security",
        list: [
          "Passwords are stored only as salted hashes, never in plain text.",
          "Sign-in uses signed session tokens with an expiry, and sessions can be ended by logging out.",
          "Google sign-in is available so you can rely on your Google account's own protections.",
          "Repeated failed login attempts are rate-limited to slow down guessing.",
          "You can change your password at any time from Settings, and reset it by email if you forget it.",
        ],
      },
      {
        title: "Encryption",
        body: "Traffic between your browser and PostMint is served over HTTPS/TLS. Data at rest is stored in our managed database.",
        note: "confirm",
        noteText:
          "Confirm before publishing: state your database encryption-at-rest configuration and TLS versions once verified with your hosting provider.",
      },
      {
        title: "Access controls",
        list: [
          "Customer data is scoped to the account that owns it; every request is checked against the signed-in user.",
          "Administrative screens are restricted to PostMint admin accounts.",
          "Access tokens for connected social accounts are stored for publishing and removed when you disconnect the account.",
        ],
      },
      {
        title: "Infrastructure",
        body: "PostMint runs as a managed cloud application with a managed database.",
        note: "confirm",
        noteText: "Confirm before publishing: name your hosting provider, region and data location.",
      },
      {
        title: "Backups and continuity",
        body: "Backup frequency, retention and restore testing.",
        note: "confirm",
        noteText:
          "Confirm before publishing: describe your actual backup schedule, retention and whether restores have been tested. Do not claim backups that are not configured.",
      },
      {
        title: "Incident management",
        body: `We monitor application errors and investigate reports of security problems. If a personal data breach affects you, we will inform you and, where required, the supervisory authority. Report a suspected vulnerability or incident to ${COMPANY.email} — please do not test against other customers' data.`,
      },
      {
        title: "Privacy by design",
        list: [
          "Card details are handled by our payment provider; PostMint never stores them.",
          "Non-essential cookies are off until you consent, and analytics is not loaded before that.",
          "You can export your data and delete your account yourself from Settings → Privacy & Data.",
          "Content is only published after you approve it.",
        ],
      },
      {
        title: "Third-party providers",
        body: "We rely on providers for hosting, payments, email and AI generation. Each is listed with its role in the Subprocessor List on the DPA page.",
      },
      {
        title: "What we do not claim",
        body: "We do not hold ISO 27001 or SOC 2 certification, and we have not published a third-party penetration test. We would rather tell you that plainly than imply a certification we do not have.",
      },
      CONTACT_SECTION,
    ],
  },

  "legal-notice": {
    title: "Legal Notice & Company Information",
    intro: "Company and contact information for PostMint, as required for online services in the Netherlands and the EU.",
    sections: [
      { title: "Company details", company: true },
      {
        title: "Complaints",
        body: `If something goes wrong — with the service, your content or your billing — email ${COMPANY.email} with your account email and what happened. We aim to acknowledge complaints promptly and to explain the outcome in writing. If you are not satisfied with our response, you can escalate to the competent Dutch consumer or data protection authority.`,
        note: "confirm",
        noteText: "Add your target response time for complaints once you have decided it (for example: within 5 working days).",
      },
      {
        title: "Documents",
        body: "Our Privacy Policy, Cookie Policy, Terms of Service, Acceptable Use Policy, Data Processing Agreement, Refund & Cancellation Policy and Trust & Security page are all linked in the footer of every page.",
      },
      CONTACT_SECTION,
    ],
  },
};

export const DOC_INDEX = [
  ["privacy-policy", "How we handle personal data, and your GDPR rights."],
  ["cookie-policy", "Which cookies we use and how to change your choice."],
  ["terms", "The agreement for using PostMint."],
  ["acceptable-use", "What you may not do with PostMint."],
  ["refund-policy", "Cancelling your subscription and when refunds apply."],
  ["dpa", "For business customers: Article 28 GDPR processing terms."],
  ["trust-security", "The security measures actually in place."],
  ["legal-notice", "Company details, contact and complaints."],
];
